/**
 * @file        src/layout/WhatsAppButton.jsx
 * @description Floating "WhatsApp us" button, fixed to the bottom-right corner on
 *              every in-app page (mounted once from AppLayout). Mirrors the widget
 *              on beyonddestination.in: tapping it opens a WhatsApp chat with the
 *              Beyond Destination desk, pre-filled with a short opening message.
 *              Uses wa.me, which resolves to the native app on mobile and to
 *              WhatsApp Web on desktop — no third-party script, no tracking.
 * @author      Kinetika Labs
 * @created     2026-07-31
 * @changelog
 *   - 2026-07-31 Claude — Initial.
 */
import React from 'react';

// Country code + number, digits only — wa.me rejects spaces, "+" and dashes.
// This is the desk the chat lands in (the WhatsApp Business profile name is set
// on WhatsApp's side, not here).
const WHATSAPP_NUMBER = '919372502300';

// Pre-filled first message. Keep it short — the customer sees it in their input
// box and can edit or delete it before sending.
const PREFILL = 'Hi Beyond Destination! I have a question about a booking.';

const HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(PREFILL)}`;

export default function WhatsAppButton() {
  return (
    <a
      href={HREF}
      target="_blank"
      rel="noreferrer"
      aria-label="Chat with us on WhatsApp"
      className="group fixed bottom-5 right-5 z-40 flex items-center gap-0 rounded-full bg-[#25D366] text-white shadow-[0_10px_28px_-8px_rgba(37,211,102,.75)] hover:bg-[#1FBE5A] transition-all print:hidden"
    >
      <span className="w-14 h-14 flex-none grid place-items-center">
        <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2m0 1.67c2.2 0 4.27.86 5.82 2.42a8.19 8.19 0 0 1 2.42 5.82c0 4.54-3.7 8.24-8.25 8.24a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.26-8.24M8.53 7.33c-.16 0-.42.06-.64.3-.22.24-.85.83-.85 2.02s.87 2.34 1 2.5c.12.17 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.16 1.52.1.46-.07 1.43-.59 1.63-1.15.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.55.12-.16.24-.62.79-.76.95-.14.17-.28.19-.52.07-.24-.12-1.02-.38-1.94-1.2-.72-.64-1.2-1.43-1.34-1.67-.14-.24-.02-.37.1-.49.11-.11.24-.28.36-.42.12-.15.16-.25.24-.41.08-.17.04-.31-.02-.43-.06-.12-.54-1.32-.76-1.8-.2-.47-.4-.4-.55-.41z"
          />
        </svg>
      </span>
      {/* Label unrolls on hover/focus (desktop). The icon alone is the whole
          target on touch, where there is no hover. */}
      <span className="max-w-0 overflow-hidden whitespace-nowrap font-bold text-sm transition-all duration-300 group-hover:max-w-[10rem] group-hover:pr-6 group-focus-visible:max-w-[10rem] group-focus-visible:pr-6">
        WhatsApp us
      </span>
    </a>
  );
}
